import React from 'react';
import AgentMarkdownContent from 'features/agent-analytics/components/AgentMarkdownContent';

/**
 * The type scale the Issue and Failure drawers share, after the run drawer:
 * small uppercase labels for sections and fields, 13px body text at a
 * readable measure, monospace blocks that wrap.
 */
export const LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]';
const BODY = 'text-[13px] leading-[1.65]';

/** A titled block of the drawer. */
export function Section({ title, aside = null, children }) {
  return (
    <section className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className={LABEL}>{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** A drawer section the Traces way: semibold title over a hairline. */
export function RuledSection({ title, aside = null, children }) {
  return (
    <section className="min-w-0">
      <div className="mb-3 flex items-baseline justify-between gap-2 border-b border-[color:var(--border-default)] pb-2">
        <h3 className="text-[13px] font-semibold text-[color:var(--text-primary)]">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Label above value, in an even grid. */
export function MetaGrid({ children }) {
  return <dl className="grid gap-x-6 gap-y-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>{children}</dl>;
}

export function MetaItem({ label, span = 1, children }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${span === 2 ? 'col-span-2' : ''}`}>
      <dt className="text-[11px] text-[color:var(--text-muted)]">{label}</dt>
      <dd className="truncate text-[13px] text-[color:var(--text-primary)]">{children}</dd>
    </div>
  );
}

/** An error or other machine text: monospace, wraps, scrolls past a screenful. */
export function CodeBlock({ children, tone = 'neutral' }) {
  const colours = tone === 'error'
    ? 'border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] text-[color:var(--feedback-error)]'
    : 'border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] text-[color:var(--text-primary)]';
  return (
    <pre className={`max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border px-3 py-2.5 font-mono text-[12px] leading-relaxed ${colours}`}>
      {children}
    </pre>
  );
}

// "What fails: …" — a short capitalised lead-in, then the text.
const LEAD_IN = /^\**([A-Z][A-Za-z0-9’'/()-]*(?: [A-Za-z0-9’'/()-]+){0,4})\**:\**[ \t]+(\S[\s\S]*)$/;

/**
 * Split text into blank-line separated blocks, keeping fenced code whole, and
 * group them under their lead-ins: Ask writes a root cause as "What fails: …",
 * "Why: …", "How to see it: …" paragraphs, and each reads as a sub-heading
 * over its text. Text before the first lead-in has no heading.
 */
export function splitLeadIns(text) {
  const blocks = [];
  let cur = [];
  let fence = false;
  String(text || '').replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    if (/^\s*(```|~~~)/.test(line)) fence = !fence;
    if (!fence && !line.trim()) {
      if (cur.length) blocks.push(cur.join('\n'));
      cur = [];
      return;
    }
    cur.push(line);
  });
  if (cur.length) blocks.push(cur.join('\n'));

  const parts = [];
  blocks.forEach((b) => {
    const m = LEAD_IN.exec(b);
    // "What fails: chat runs …" reads as "Chat runs …" under its heading.
    if (m) parts.push({ heading: m[1], body: [m[2].replace(/^[a-z]+(?= )/, (w) => w[0].toUpperCase() + w.slice(1))] });
    else if (parts.length) parts[parts.length - 1].body.push(b);
    else parts.push({ heading: null, body: [b] });
  });
  return parts.map((p) => ({ heading: p.heading, body: p.body.join('\n\n') }));
}

/**
 * Prose Ask wrote (root cause, suggested change): markdown, rendered the way
 * trace output is, with lead-ins as sub-headings. Single line breaks are kept.
 */
export function DetailText({ children }) {
  const parts = splitLeadIns(children);
  if (!parts.length) return <p className="text-[13px] text-[color:var(--text-muted)]">—</p>;
  return (
    <div className="flex flex-col gap-3">
      {parts.map((p, i) => (
        <div key={i} className="min-w-0">
          {p.heading ? <h4 className="mb-1 text-[13px] font-semibold text-[color:var(--text-primary)]">{p.heading}</h4> : null}
          <AgentMarkdownContent className={`${BODY} break-words [&_p]:whitespace-pre-line`}>
            {p.body}
          </AgentMarkdownContent>
        </div>
      ))}
    </div>
  );
}
